import React, { useEffect, useState, useMemo, useRef, ReactNode } from 'react'
import { useHistory } from 'react-router-dom'
import { ThemeProvider, useMediaQuery, useTheme } from '@mui/material'

import { getTheme } from 'themes'
import { sortGridLayout } from 'utils/sortHelper'
import {
  formatResourceFilterOptions,
  getFilterFromSegmentPaths,
  isCustomPeriodFilter,
  isPeriodFilterEnum
} from 'utils/functions'
import { useParams } from 'utils/hooks'
import { downloadPNG } from './utils'

/* Atoms */
import Box from 'components_new/atoms/Box'

/* Organisms */
import DashboardGrid from 'components_new/organisms/DashboardGrid'
import EditModeBar from 'components_new/organisms/EditModeBar'
import MobileTabSelector from 'components_new/organisms/MobileTabSelector'
import PublishBar from 'components_new/organisms/PublishBar'
import DataDialog from 'components_new/organisms/DataDialog'
import { buildKpiOptionSource } from 'components_new/organisms/DataDialog/builders'
import { resolveWidgetContextParams } from 'components_new/organisms/DataDialog/widgetContext'
import Widget from 'components_new/organisms/Widget'

import DesktopLoading from './desktop/loading'
import MobileLoading from './mobile/loading'

/* Templates */
import { useGridRef } from 'components_new/templates/PageLayout'

/* Actions */
import * as UserEvents from 'redux/api/internal/UserEvents'
import { useDashboardActions } from 'redux/actions/Dashboards'
import { useDashboardWidgetActions } from 'redux/actions/Widgets/DashboardWidgets'

/* Hooks */
import {
  useCustomSegmentByMapper,
  useDashboardFilter,
  useEditMode,
  usePeriodFilter
} from 'redux/hooks/DashboardPage'
import { useDashboardGroupHasManyDashboards } from 'redux/hooks/DashboardGroups'
import { useWidgetsInDashboard } from 'redux/hooks/DashboardWidgets'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useGlobalFilterSaveKey } from 'redux/hooks/GlobalFilter'
import { useDashboardFetchStatus } from 'redux/hooks/Dashboards'
import {
  useResourceFilterData,
  useResourceFilterOptions
} from 'redux/hooks/ResourceFilter'
import {
  useResetDashboardUserInput,
  useSetCustomWidgetSegmentBy,
  useSetDashboardFilter,
  useSetDashboardPeriodFilter
} from 'helpers/hooks/Dashboards'

/* Types */
import { Action, Context } from 'types/GlobalUserEvents'
import {
  CustomPeriodFilter,
  InnerWidgetPatchBody,
  ParsedSegmentPath,
  SegmentPeriod,
  TCoreWidget,
  TWidgetSettings,
  WidgetType
} from 'types/GlobalWidget'
import { DashboardVariant, TDashboard } from 'types/GlobalDashboard'
import { DashboardFilter } from 'types/GlobalDashboardFilter'
import { TResourceFilter } from 'types/GlobalDashboardGroup'
import { TCustomization } from 'types/GlobalCustomization'
import { Filters } from 'utils/types'

interface DashboardProps {
  customization: TCustomization
  dashboard: TDashboard | null
  dashboardFilters: TResourceFilter[]
  fetchWidgetData: (widget: TCoreWidget) => void
  variant: DashboardVariant
}

const Dashboard = (props: DashboardProps) => {
  const {
    customization,
    dashboard,
    dashboardFilters,
    fetchWidgetData,
    variant
  } = props

  const resourceFilterOptions = useResourceFilterOptions()
  const resourceFilterData = useResourceFilterData()

  const params = useParams<{ id: string }>()
  const dashboardFetchStatus = useDashboardFetchStatus(params.id)
  const history = useHistory()
  const editMode = useEditMode()
  const gridRef = useGridRef()
  const multipleTabs = useDashboardGroupHasManyDashboards(
    dashboard?.dashboard_group_id || null
  )
  const dashboardFilter = useDashboardFilter()
  const periodFilter = usePeriodFilter()
  const customSegmentByMapper = useCustomSegmentByMapper()
  const widgets = useWidgetsInDashboard(params.id)
  const { tryUpdateDashboardLayout, tryUploadDashboardThumbnail } =
    useDashboardActions()
  const { tryUpdateDashboardWidget } = useDashboardWidgetActions()
  const globalFilterSaveKey = useGlobalFilterSaveKey()

  const [underlyingContent, setUnderlyingContent] = useState<{
    widgetSettings: TWidgetSettings
    initialKpiId: string
    segmentLabel: string | number | null
    segmentFilters: Filters
    segmentPeriod: CustomPeriodFilter | null
  } | null>(null)

  const { isAdmin, isCompanyGroup } = useAccountSettings()

  const setDashboardFilter = useSetDashboardFilter()
  const setDashboardPeriodFilter = useSetDashboardPeriodFilter()
  const setCustomWidgetSegmentBy = useSetCustomWidgetSegmentBy(dashboard)
  const resetDashboardUserInput = useResetDashboardUserInput(dashboard)

  // Needs to be a ref
  const isDownloadReadyRef = useRef(false)

  useEffect(() => {
    const allFetched = widgets.every(
      (widget) =>
        widget.data ||
        [WidgetType.TEXT, WidgetType.COMMENT, WidgetType.IMAGE].includes(
          widget.type
        ) ||
        widget.status?.broken
    )

    isDownloadReadyRef.current = widgets.length > 0 && allFetched
  }, [widgets, params.id])

  useEffect(() => {
    // Listen to route changes
    const unlisten = history.listen(() => {
      // Save the thumbnail before the route changes
      if (gridRef.current && isDownloadReadyRef.current) {
        downloadPNG(gridRef.current, (thumbnail: Blob | null) => {
          if (thumbnail && !isCompanyGroup && dashboard) {
            tryUploadDashboardThumbnail(dashboard.id, thumbnail)
          }
        })
      }
    })

    // Clean up event listener when the component unmounts
    return () => {
      unlisten()
    }
  }, [dashboard?.id])

  // Sort widgets by layout for rendering
  const sortedWidgets = useMemo(() => {
    return [...widgets].sort((a, b) => sortGridLayout(a.layout, b.layout))
  }, [widgets])

  const layout: { x: number; y: number; w: number; h: number; i: string }[] =
    useMemo(() => {
      return widgets.map((widget) => {
        return {
          x: widget.layout.x,
          y: widget.layout.y,
          w: widget.layout.width,
          h: widget.layout.height,
          i: widget.id
        }
      })
    }, [widgets])

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('mobile'), {
    noSsr: true
  })

  const widgetHeight = useMemo(() => {
    if (!isMobile) return undefined

    return 256
  }, [isMobile])

  const dashboardFilterOptions = formatResourceFilterOptions(
    dashboardFilters,
    resourceFilterOptions,
    resourceFilterData,
    globalFilterSaveKey
  )

  // loading
  if (
    dashboardFetchStatus.status === 'fetching' ||
    dashboardFetchStatus.status === 'idle' ||
    !dashboard
  ) {
    return isMobile ? (
      <MobileLoading customization={customization} />
    ) : (
      <DesktopLoading customization={customization} />
    )
  }

  // success
  return (
    <ContentContainer displayTabs={multipleTabs} isMobile={isMobile}>
      <Box
        sx={{
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {isMobile ? null : (
          <>
            <PublishBar />
            <EditModeBar
              availableSpace={dashboard.available_space ?? false}
              dashboard={dashboard}
            />
          </>
        )}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            position: 'relative',
            height: '100%',
            width: '100%',
            borderLeft: editMode ? '1px solid' : undefined,
            borderBottom: editMode ? '1px solid' : undefined,
            borderRight: editMode ? '1px solid' : undefined,
            borderColor: 'primary.main'
          }}
        >
          <DashboardGrid
            customization={customization}
            layout={layout}
            title={dashboard.title}
            updateLayout={(layout) =>
              tryUpdateDashboardLayout(dashboard.id, {
                layout: layout.map((l) => {
                  return {
                    id: l.i,
                    width: l.w,
                    height: l.h,
                    x: l.x,
                    y: l.y
                  }
                })
              })
            }
            variant={variant}
          >
            {({ scaleFactor }) => {
              return sortedWidgets.map((widget) => {
                const customSegmentBy = customSegmentByMapper[widget.id] || null

                return (
                  <Box
                    key={widget.id}
                    sx={{
                      position: 'relative',
                      height: widgetHeight,
                      minHeight: widgetHeight
                    }}
                  >
                    <Widget
                      availableSpace={dashboard?.available_space ?? null}
                      customization={customization}
                      customPeriodFilter={
                        periodFilter && isCustomPeriodFilter(periodFilter)
                          ? periodFilter
                          : null
                      }
                      customSegmentBy={customSegmentBy}
                      dashboard={dashboard}
                      dashboardFilter={dashboardFilter}
                      dashboardFilterOptions={dashboardFilterOptions}
                      editMode={editMode}
                      embedded={variant.embedded}
                      fetchWidgetData={fetchWidgetData}
                      showUnderlyingContent={(
                        widgetSettings: TWidgetSettings,
                        segmentPaths: ParsedSegmentPath[],
                        initialKpiOptionId: string | null
                      ) => {
                        const initialId = initialKpiOptionId
                          ? initialKpiOptionId
                          : widgetSettings.kpi_options[0].id

                        const segmentFilters =
                          getFilterFromSegmentPaths(segmentPaths)

                        const pathWithPeriod = segmentPaths.find(
                          (item) => item.period
                        )

                        let segmentPeriod: CustomPeriodFilter | null = null

                        if (pathWithPeriod) {
                          const { from_date, to_date } =
                            pathWithPeriod.period as SegmentPeriod

                          segmentPeriod = {
                            from: from_date,
                            to: to_date
                          }
                        }

                        setUnderlyingContent({
                          widgetSettings,
                          initialKpiId: initialId,
                          segmentLabel: segmentPaths[0]?.display_label,
                          segmentFilters,
                          segmentPeriod
                        })
                      }}
                      isAdmin={isAdmin}
                      periodFilter={
                        periodFilter && isPeriodFilterEnum(periodFilter)
                          ? periodFilter
                          : null
                      }
                      resetAllDashboardTempStates={resetDashboardUserInput}
                      scaleFactor={scaleFactor}
                      setCustomSegmentBy={(
                        attributeOptionId,
                        dbFilter,
                        pFilter
                      ) => {
                        setCustomWidgetSegmentBy(
                          widget,
                          attributeOptionId,
                          dbFilter,
                          pFilter
                        )
                      }}
                      setDashboardFilter={(
                        dbFilter: DashboardFilter,
                        periodFilter?: CustomPeriodFilter
                      ) => {
                        UserEvents.log({
                          action: periodFilter
                            ? Action.TIME_PERIOD_FILTERED
                            : Action.SELECTION_FILTERED,
                          context: Context.FROM_WIDGET,
                          dashboard_id: widget.dashboard_id,
                          dashboard_group_id: null
                        })

                        setDashboardFilter(dbFilter, periodFilter)
                      }}
                      setPeriodFilter={setDashboardPeriodFilter}
                      variantType={variant.type}
                      updateWidget={(
                        widgetId: string,
                        attribute: InnerWidgetPatchBody
                      ) => {
                        tryUpdateDashboardWidget(
                          widgetId,
                          { data: { ...attribute } },
                          !!periodFilter
                        )
                      }}
                      widget={widget}
                    />
                  </Box>
                )
              })
            }}
          </DashboardGrid>
        </Box>

        {/*-- underlying data dialog --*/}
        <ThemeProvider theme={getTheme('light')}>
          <DataDialog
            source={
              underlyingContent
                ? buildKpiOptionSource(
                  underlyingContent.widgetSettings.kpi_options,
                  underlyingContent.initialKpiId
                )
                : null
            }
            {...(underlyingContent
              ? resolveWidgetContextParams({
                widgetSettings: underlyingContent.widgetSettings,
                dashboardFilter,
                periodFilter
              })
              : { baseFilters: undefined, basePeriod: null })}
            eventContext={{
              context: Context.FROM_DASHBOARD_PAGE,
              dashboardId: dashboard.id,
              dashboardGroupId: dashboard.dashboard_group_id
            }}
            onClose={() => setUnderlyingContent(null)}
            segmentFilters={underlyingContent?.segmentFilters ?? {}}
            segmentLabel={underlyingContent?.segmentLabel ?? null}
            segmentPeriod={underlyingContent?.segmentPeriod ?? null}
          />
        </ThemeProvider>
      </Box>
    </ContentContainer>
  )
}

interface ContentContainerProps {
  children: ReactNode
  displayTabs?: boolean
  isMobile: boolean
}

// Should later be included in- and replaced by desktop and mobile files:
const ContentContainer = (props: ContentContainerProps) => {
  const { children, displayTabs, isMobile } = props

  if (isMobile) {
    return (
      <Box sx={{ minHeight: '100%' }}>
        {displayTabs ? <MobileTabSelector /> : null}
        {children}
      </Box>
    )
  } else {
    return <>{children}</>
  }
}

export default Dashboard
